import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ChangeDetectorRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from "../../../../core/services/users.service";
import { User } from "../../../../core/models/user.interface";
import { MatIconModule } from "@angular/material/icon";
import { LoaderService } from "../../../../core/shared/services/loader.service";
import { formatDate } from "@angular/common";
import { USER_TYPES, SUBSCRIPTION_TYPES } from "../../../../core/shared/constants";
import { FormsModule } from "@angular/forms";
import { AuthService } from "../../../../core/services/auth.service";
import { AlignetPaymentGatewayService } from "../../../../core/services/alignet-payment-gateway.service";
import { DiscountService, DiscountCode } from "../../../../core/services/discount.service";
import { EmailService, EmailData } from "../../../../core/services/email.service";
import { environment } from "../../../../../environments/environment";
import Swal from 'sweetalert2';

interface TimeSlot {
  time: string;
  displayTime: string;
  isBooked: boolean;
  isPastTime: boolean;
  selected: boolean;
}

interface CalendarDay {
  dayNumber: number;
  date: Date;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isAvailable: boolean;
}

@Component({
  selector: "app-mentor-profile",
  standalone: true,
  imports: [CommonModule, MatIconModule, FormsModule],
  templateUrl: "./mentor-profile.component.html",
  styleUrl: "./mentor-profile.component.scss",
})
export class MentorProfileComponent implements OnInit {
  mentor: User | null | undefined = null;
  isLoading = true;
  mentorId: string = "";
  private loader = inject(LoaderService);

  // Calendario mensual
  currentMonth = new Date();
  calendarDays: CalendarDay[] = [];
  selectedCalendarDay: CalendarDay | null = null;
  availableTimeSlots: TimeSlot[] = [];
  availableTimeSlotsFormatted: string = 'Ninguno';
  selectedTimeSlot: TimeSlot | null = null;

  // Modales
  showSchedulingModal = false;
  showPurchaseSummaryModal = false;
  showSuccessModal = false;
  currentStep = 1;
  selectedPaymentMethod = 'card';
  saveCard = false;
  
  // Términos y condiciones
  acceptTerms = false;
  showTermsModal = false;
  
  // Modal de política de cancelación
  showCancellationPolicyModal = false;
  
  // Pasarela de pago
  paymentFormLoaded = false;
  paymentFormError = false;

  // Propiedades para control de selección de horarios
  selectedTimeSlots: TimeSlot[] = [];
  maxTimeSlotsAllowed: number = 1;

  // Propiedades para códigos de descuento
  discountCode: string = '';
  appliedDiscount: DiscountCode | null = null;
  discountAmount: number = 0;
  finalAmount: number = 0;
  isApplyingDiscount: boolean = false;

  // Propiedades para descuento DESCEXECUTIVE
  executiveDiscount: DiscountCode | null = null;
  useExecutiveDiscount: boolean = false;
  isLoadingExecutiveDiscount: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private alignetPaymentService: AlignetPaymentGatewayService,
    private discountService: DiscountService,
    private emailService: EmailService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params["id"]) {
        this.mentorId = params["id"];
        this.loadMentorDetails();
      } else {
        this.router.navigate(["/dashboard/mentors"]);
      }
    });

    // Verificar si se debe abrir automáticamente el modal de programación
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams['openScheduling'] === 'true') {
        this.shouldOpenSchedulingModal = true;
      }
    });

    // Configurar límite de horarios según tipo de suscripción
    this.configureTimeSlotLimits();
  }

  // Variable para controlar si se debe abrir el modal automáticamente
  private shouldOpenSchedulingModal = false;

  // Función para verificar y abrir el modal de programación
  private checkAndOpenSchedulingModal(): void {
    if (this.shouldOpenSchedulingModal && this.mentor) {
      this.showSchedulingModal = true;
      this.shouldOpenSchedulingModal = false; // Reset para evitar que se abra nuevamente
    }
  }

  loadMentorDetails(): void {
    this.isLoading = true;
    this.loader.show();
    
    this.usersService.findById(this.mentorId).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.mentor = response.data;

          if (!this.mentor.coachProfileData) {
            this.mentor.coachProfileData = { eventsAndAppearances: [] };
          }

          this.generateCalendarDays();
          
          // Verificar si se debe abrir el modal de programación después de cargar los datos
          this.checkAndOpenSchedulingModal();
        }
        this.isLoading = false;
        this.loader.hide();
      },
      error: (err) => {
        console.error("Error al cargar detalles del mentor:", err);
        this.isLoading = false;
        this.loader.hide();
        this.router.navigate(["/dashboard/mentors"]);
      },
    });
  }

  // ===== CALENDARIO MENSUAL =====
  getCurrentMonth(): string {
    return this.currentMonth.toLocaleDateString('es-ES', { 
      month: 'long', 
      year: 'numeric' 
    }).replace(/^\w/, c => c.toUpperCase());
  }

  navigateToPreviousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.generateCalendarDays();
    this.resetSelections();
  }

  navigateToNextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.generateCalendarDays();
    this.resetSelections();
  }

  generateCalendarDays(): void {
    this.calendarDays = [];
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    
    // Obtener fecha actual en zona horaria de Lima (America/Lima UTC-5)
    const now = new Date();
    const limaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Lima' }));
    const today = new Date(limaTime.getFullYear(), limaTime.getMonth(), limaTime.getDate());
    today.setHours(0, 0, 0, 0);
    
    // Calcular la fecha mínima permitida (2 días después de hoy - 72 horas)
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    minDate.setHours(0, 0, 0, 0);

    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Primer día de la semana (lunes = 1)
    const firstDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay();
    
    // Días del mes anterior para completar la primera semana
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      this.calendarDays.push({
        dayNumber: date.getDate(),
        date: date,
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
        isAvailable: false
      });
    }

    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const isToday = date.getTime() === today.getTime();
      
      // Verificar disponibilidad del mentor
      const dateStr = formatDate(date, 'yyyy-MM-dd', 'en-US');
      const hasMentorAvailability = this.mentor?.coachAvailability?.some(
        item => item.day === dateStr
      ) || false;
      
      // Un día solo está disponible si:
      // 1. La fecha es mayor o igual a minDate (al menos 3 días después de hoy)
      // 2. El mentor tiene disponibilidad en ese día
      const isAvailable = date >= minDate && hasMentorAvailability;

      this.calendarDays.push({
        dayNumber: day,
        date: date,
        isCurrentMonth: true,
        isSelected: false,
        isToday: isToday,
        isAvailable: isAvailable
      });
    }

    // Completar la última semana
    const remainingDays = 42 - this.calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      this.calendarDays.push({
        dayNumber: day,
        date: date,
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
        isAvailable: false
      });
    }
  }

  selectCalendarDay(day: CalendarDay): void {
    if (!day.isAvailable || !day.isCurrentMonth) return;

    const dateStr = formatDate(day.date, 'yyyy-MM-dd', 'en-US');
    const hasMentorAvailability = this.mentor?.coachAvailability?.some(
      item => item.day === dateStr
    );

    if (!hasMentorAvailability) return;

    // Resetear selección anterior
    this.calendarDays.forEach(d => d.isSelected = false);
    
    // Seleccionar nuevo día
    day.isSelected = true;
    this.selectedCalendarDay = day;
    
    // Generar horarios disponibles
    this.generateAvailableTimeSlots();
  }

  // ===== GENERACIÓN DE HORARIOS =====
  generateAvailableTimeSlots(): void {
    if (!this.selectedCalendarDay) {
      this.availableTimeSlots = [];
      this.availableTimeSlotsFormatted = 'Ninguno';
      return;
    }

    const selectedDateStr = formatDate(this.selectedCalendarDay.date, 'yyyy-MM-dd', 'en-US');
    
    // Buscar disponibilidad del mentor
    const disponibilidad = this.mentor?.coachAvailability?.find(appt => appt.day === selectedDateStr);
    
    if (!disponibilidad || !disponibilidad.times || disponibilidad.times.length === 0) {
      this.availableTimeSlots = [];
      this.availableTimeSlotsFormatted = 'Ninguno';
      this.cdr.detectChanges();
      return;
    }

    const today = new Date();
    const isToday = this.selectedCalendarDay.date.toDateString() === today.toDateString();

    // Obtener citas ocupadas para esta fecha
    const citasOcupadas = this.mentor?.coachAppointments?.filter(
      appt => appt.date === selectedDateStr
    ) || [];

    // Normalizar horarios reservados para comparación
    const bookedTimes = citasOcupadas.map((appointment: any) => 
      this.normalizeTimeFormat(appointment.time)
    );

    // Filtrar horarios disponibles
    const horariosDisponibles = disponibilidad.times.filter(time => {
      // Normalizar el tiempo para comparación
      const normalizedTime = this.normalizeTimeFormat(time);
      
      // Verificar si está reservado usando comparación normalizada
      const isBooked = bookedTimes.includes(normalizedTime);
      
      // Verificar si ya pasó (solo para hoy)
      let isPastTime = false;
      if (isToday) {
        // Convertir tiempo a formato 24h para comparación
        let hours: number, minutes: number;
        
        if (time.includes('AM') || time.includes('PM')) {
          // Formato 12h con AM/PM
          const parts = time.split(' ');
          const [timePart, period] = parts;
          const [h, m] = timePart.split(':').map(Number);
          
          if (period === 'AM') {
            hours = h === 12 ? 0 : h;
          } else {
            hours = h === 12 ? 12 : h + 12;
          }
          minutes = m;
        } else {
          // Formato 24h
          [hours, minutes] = time.split(':').map(Number);
        }
        
        // Crear la fecha del horario usando la fecha seleccionada
        const currentTime = new Date();
        const todaySlotTime = new Date();
        todaySlotTime.setHours(hours, minutes, 0, 0);
        
        // Solo considerar pasado si el horario ya pasó hoy (con 30 minutos de margen)
        const thirtyMinutesFromNow = new Date(currentTime.getTime() + 30 * 60 * 1000);
        isPastTime = todaySlotTime <= thirtyMinutesFromNow;
      }

      return !isBooked && !isPastTime;
    });


    // Crear objetos TimeSlot
    const timeSlots = horariosDisponibles.map(time => ({
      time: time,
      displayTime: this.formatTimeToDisplay(time),
      isBooked: false, // Ya filtramos los reservados
      isPastTime: false, // Ya filtramos los pasados
      selected: false
    }));

    // Asignar horarios
    this.availableTimeSlots = timeSlots;
    
    // Actualizar propiedad formateada (usar displayTime que incluye AM/PM)
    if (timeSlots.length > 0) {
      this.availableTimeSlotsFormatted = timeSlots.map(s => s.displayTime).join(', ');
    } else {
      this.availableTimeSlotsFormatted = 'Ninguno';
    }

    // Forzar actualización de UI
    this.cdr.detectChanges();
  }

  // ===== CONFIGURACIÓN DE LÍMITES DE HORARIOS =====
  private configureTimeSlotLimits(): void {
    const currentUser = this.authService.getUser();
    if (currentUser && currentUser.type && currentUser.type.suscriptionType) {
      switch (currentUser.type.suscriptionType.id) {
        case SUBSCRIPTION_TYPES.Freemium.id.toString():
          this.maxTimeSlotsAllowed = 1;
          break;
        case SUBSCRIPTION_TYPES.Professional.id.toString():
        case SUBSCRIPTION_TYPES.Executive.id.toString():
          this.maxTimeSlotsAllowed = 3;
          break;
        default:
          this.maxTimeSlotsAllowed = 1;
      }
    } else {
      this.maxTimeSlotsAllowed = 1; // Por defecto, solo 1 horario
    }
  }

  // ===== SELECCIÓN DE HORARIOS =====
  selectTimeSlot(slot: TimeSlot): void {
    if (slot.isBooked || slot.isPastTime) return;

    // Si el horario ya está seleccionado, deseleccionarlo
    if (slot.selected) {
      slot.selected = false;
      this.selectedTimeSlots = this.selectedTimeSlots.filter(s => s !== slot);
      this.selectedTimeSlot = this.selectedTimeSlots.length > 0 ? this.selectedTimeSlots[0] : null;
      return;
    }

    // Verificar si ya se alcanzó el límite máximo de horarios
    if (this.selectedTimeSlots.length >= this.maxTimeSlotsAllowed) {
      // Mostrar mensaje de error según el tipo de suscripción
      let message = '';
      if (this.maxTimeSlotsAllowed === 1) {
        message = 'Los usuarios Freemium solo pueden seleccionar 1 hora por experto al día.';
      } else {
        message = `Ya has seleccionado el máximo de ${this.maxTimeSlotsAllowed} horas permitidas para tu suscripción.`;
      }
      
      // Mostrar notificación con SweetAlert2
      Swal.fire({
        icon: 'warning',
        title: 'Límite de horarios alcanzado',
        text: message,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#CE0024'
      });
      return;
    }

    // Seleccionar nuevo horario
    slot.selected = true;
    this.selectedTimeSlots.push(slot);
    
    // Mantener compatibilidad con el código existente
    this.selectedTimeSlot = slot;
  }

  // ===== UTILIDADES =====
  /**
   * Formatea el tiempo para mostrar en formato 12h con AM/PM
   * Similar a mi-mentor.component.ts
   */
  private formatTimeToDisplay(time: string): string {
    // Si ya está en formato 12h con AM/PM, mantenerlo así
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    
    // Si está en formato 24h, convertir a 12h
    const [hour, minute] = time.split(':');
    const h = parseInt(hour, 10);
    const period = h >= 12 ? 'PM' : 'AM';
    const adjustedHour = h % 12 === 0 ? 12 : h % 12;
    return `${adjustedHour}:${minute} ${period}`;
  }

  /**
   * Normaliza el formato de tiempo para comparaciones consistentes
   * Convierte "2:00 PM" a "02:00 PM" para mantener consistencia
   * Similar a mi-mentor.component.ts
   */
  private normalizeTimeFormat(time: string): string {
    if (!time) return time;
    
    // Si ya está normalizado, retornarlo
    if (time.match(/^\d{2}:\d{2} (AM|PM)$/)) {
      return time;
    }
    
    // Dividir por el espacio para separar tiempo y AM/PM
    const parts = time.split(' ');
    if (parts.length !== 2) {
      // Si no tiene AM/PM, intentar convertir desde formato 24h
      const [hours, minutes] = time.split(':');
      if (hours && minutes) {
        const h = parseInt(hours, 10);
        const period = h >= 12 ? 'PM' : 'AM';
        const adjustedHour = h % 12 === 0 ? 12 : h % 12;
        return `${String(adjustedHour).padStart(2, '0')}:${minutes} ${period}`;
      }
      return time;
    }
    
    const [timePart, period] = parts;
    const [hours, minutes] = timePart.split(':');
    
    // Normalizar las horas con padding de cero
    const normalizedHours = hours.padStart(2, '0');
    
    return `${normalizedHours}:${minutes} ${period}`;
  }

  /**
   * Convierte tiempo a formato 12h con AM/PM para enviar al backend
   * Similar a mi-mentor.component.ts convertTimeTo24Hour
   */
  private convertTimeTo12Hour(time: string): string {
    // Si ya está en formato 12h con AM/PM, mantenerlo así
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    
    // Si está en formato 24h, convertir a 12h
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    
    if (hour === 0) {
      return `12:${minutes} AM`;
    } else if (hour < 12) {
      return `${hour}:${minutes} AM`;
    } else if (hour === 12) {
      return `12:${minutes} PM`;
    } else {
      return `${hour - 12}:${minutes} PM`;
    }
  }

  // ===== OBTENER INFORMACIÓN DE SUSCRIPCIÓN =====
  getUserSubscriptionInfo(): string {
    const currentUser = this.authService.getUser();
    if (!currentUser || !currentUser.type || !currentUser.type.suscriptionType) {
      return 'Freemium (1 hora por experto al día)';
    }

    switch (currentUser.type.suscriptionType.id) {
      case SUBSCRIPTION_TYPES.Freemium.id.toString():
        return 'Freemium (1 hora por experto al día)';
      case SUBSCRIPTION_TYPES.Professional.id.toString():
        return 'Professional (hasta 3 horas por experto al día)';
      case SUBSCRIPTION_TYPES.Executive.id.toString():
        return 'Executive (hasta 3 horas por experto al día)';
      default:
        return 'Freemium (1 hora por experto al día)';
    }
  }

  // ===== RESETEAR SELECCIONES =====
  resetSelections(): void {
    this.selectedCalendarDay = null;
    this.selectedTimeSlot = null;
    this.selectedTimeSlots = [];
    this.availableTimeSlots = [];
    this.availableTimeSlotsFormatted = 'Ninguno';
  }

  // ===== MODALES =====
  scrollToScheduling(): void {
    this.showSchedulingModal = true;
    if (this.calendarDays.length === 0) {
      this.generateCalendarDays();
    }
  }

  closeSchedulingModal(): void {
    this.showSchedulingModal = false;
    this.resetSelections();
  }

  showPurchaseSummary(): void {
    this.showPurchaseSummaryModal = true;
    this.loadExecutiveDiscount();
  }

  closePurchaseSummaryModal(): void {
    this.showPurchaseSummaryModal = false;
    this.currentStep = 1;
    this.resetSelections();
    // Resetear el checkbox del descuento DESCEXECUTIVE
    this.useExecutiveDiscount = false;
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
  }

  viewInCalendar(): void {
    // Cerrar el modal y navegar a la lista de mentores
    this.closeSuccessModal();
    this.router.navigate([this.getDashboardRoute()]);
  }

  goToPayment(): void {
    if (this.acceptTerms) {
      // Mostrar primero el modal de política de cancelación
      this.showCancellationPolicyModal = true;
    }
  }
  
  // Cerrar modal de política de cancelación y volver
  closeCancellationPolicyModal(): void {
    this.showCancellationPolicyModal = false;
  }
  
  // Confirmar política y continuar al pago
  confirmCancellationPolicy(): void {
    this.showCancellationPolicyModal = false;
    this.onGoToPayment();
  }

  goBackToSummary(): void {
    this.currentStep = 1;
  }

  // ===== PAGO =====
  getSelectedDateFormatted(): string {
    if (!this.selectedCalendarDay) return '';
    
    const date = this.selectedCalendarDay.date;
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    };
    
    return date.toLocaleDateString('es-ES', options);
  }

  /**
   * Muestra modal de error cuando falla el pago
   */
  showPaymentErrorModal(): void {
    Swal.fire({
      icon: 'error',
      title: 'Error en el pago',
      text: 'Hubo un problema al procesar tu pago. Por favor, intenta nuevamente más tarde o contacta con soporte si el problema persiste.',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#d33',
      showCancelButton: true,
      cancelButtonText: 'Intentar de nuevo',
      cancelButtonColor: '#3085d6'
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        // Usuario quiere intentar de nuevo - mostrar modal de resumen de compra
        this.showPurchaseSummary();
      }
    });
  }

  confirmPayment(): void {
    if (!this.mentor?._id || !this.selectedCalendarDay || this.selectedTimeSlots.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Faltan datos para reservar la cita',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    if (!this.mentor.type || (this.mentor.type.name !== 'Coach' && this.mentor.type.name !== 'Experto')) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'El usuario seleccionado no es un mentor o Experto válido',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const currentUser = this.authService.getAuthUser();
    if (!currentUser) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Usuario no autenticado',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const userType = this.mentor?.type?.name === 'Coach' ? 'mentor' : 'Experto';
    const selectedDate = formatDate(this.selectedCalendarDay.date, 'yyyy-MM-dd', 'en-US');
    
    // Crear array de citas para reservar
    const appointmentsToBook = this.selectedTimeSlots.map(slot => ({
      date: selectedDate,
      time: this.convertTimeTo12Hour(slot.displayTime), // Asegurar formato 12h con AM/PM
      clientName: currentUser.name || 'Cliente',
      clientId: currentUser.id
    }));

    //alert(`Procesando pago y reservando ${this.selectedTimeSlots.length} cita(s) con tu ${userType}...`);

    // Reservar todas las citas seleccionadas
    let completedBookings = 0;
    let failedBookings = 0;

    // Si se está usando el descuento DESCEXECUTIVE, incrementar el contador de usos
    if (this.useExecutiveDiscount && this.executiveDiscount) {
      this.discountService.incrementDiscountUsage('DESCEXECUTIVE').subscribe({
        next: () => {
          console.log('Uso del descuento DESCEXECUTIVE incrementado');
          // Recargar el descuento para actualizar los usos disponibles
          this.loadExecutiveDiscount();
        },
        error: (error) => {
          console.error('Error al incrementar uso del descuento DESCEXECUTIVE:', error);
        }
      });
    }

    appointmentsToBook.forEach((appointmentData, index) => {
      this.usersService.bookAppointment(
        this.mentor!._id, 
        appointmentData, 
        currentUser.id
      ).subscribe({
        next: (response) => {
          console.log(`Cita ${index + 1} reservada exitosamente:`, response);
          completedBookings++;
          
          // Si todas las citas se completaron, mostrar modal de éxito
          if (completedBookings === appointmentsToBook.length) {
            this.closePurchaseSummaryModal();
            this.showSuccessModal = true;
            // Resetear el checkbox del descuento DESCEXECUTIVE
            this.useExecutiveDiscount = false;
          }
        },
        error: (error) => {
          console.error(`Error al reservar la cita ${index + 1}:`, error);
          failedBookings++;
          
          // Si todas las citas fallaron, mostrar error
          if (failedBookings === appointmentsToBook.length) {
            Swal.fire({
              icon: 'error',
              title: 'Error al reservar citas',
              text: `Error al reservar las citas con tu ${userType}: ${error.error?.message || 'Error desconocido'}`,
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#d33'
            });
          }
        }
      });
    });
  }

  // ===== TÉRMINOS Y CONDICIONES =====
  openTermsModal(): void {
    this.showTermsModal = true;
  }

  closeTermsModal(): void {
    this.showTermsModal = false;
  }

  // Obtener fecha actual formateada para términos y condiciones
  getCurrentDate(): string {
    return new Date().toLocaleDateString('es-ES');
  }

  // ===== PASARELA DE PAGO =====
  
  /**
   * Formatea el monto para Alignet (convierte a centavos)
   */
  private formatearMontoParaAlignet(monto: number): number {
    // Convertir el monto a centavos (multiplicar por 100)
    return Math.round(monto * 100);
  }

  async cargarFormulario(target: HTMLElement, loadingEl: HTMLElement, monto: number): Promise<void> {
    try {
      // Obtener datos del usuario autenticado
      const currentUser = this.authService.getAuthUser();
      
      // Datos del cliente
      const customerData = {
        firstName: currentUser?.firstName || 'Cliente',
        lastName: currentUser?.lastName || 'Usuario',
        email: currentUser?.email || 'cliente@phoenix.com',
        phone: '999999999', // Usar teléfono por defecto ya que no está en AuthUser
        address: 'Av. Principal 123',
        city: 'Lima',
        state: 'Lima',
        country: 'Peru'
      };

      // Formatear el monto para Alignet (agregar 00 al final)
      const montoFormateado = this.formatearMontoParaAlignet(monto);
      
      console.log(customerData);
      console.log('Monto original:', monto);
      console.log('Monto formateado:', montoFormateado);
      console.log(target);
      console.log(loadingEl);
      // Preparar información del pago con fechas y horas seleccionadas
      const selectedDate = this.selectedCalendarDay ? 
        formatDate(this.selectedCalendarDay.date, 'yyyy-MM-dd', 'en-US') : '';
      
      const selectedTimes = this.selectedTimeSlots.map(slot => slot.displayTime).join(', ');
      
      // Debug: Log de los datos seleccionados
      console.log('🔍 Debug cargarFormulario:');
      console.log('selectedCalendarDay:', this.selectedCalendarDay);
      console.log('selectedTimeSlots:', this.selectedTimeSlots);
      console.log('selectedDate:', selectedDate);
      console.log('selectedTimes:', selectedTimes);
      
      const paymentInfo = {
        userId: this.authService.getAuthUser()?.id || 'unknown',
        planId: 'experto-session',
        planName: `Sesión con ${this.mentor?.name || 'Mentor'} Experto Phoenix`,
        frequency: 'one-time',
        sessionDate: selectedDate,
        sessionTime: selectedTimes
      };
      
      console.log('📋 paymentInfo final:', paymentInfo);

            // Usar el servicio de Alignet para cargar el formulario
      await this.alignetPaymentService.cargarFormulario(
        target,
        loadingEl,
        montoFormateado,
        customerData,
        paymentInfo,
        () => this.confirmPayment(), // Callback que se ejecuta después de guardar el pago
        () => this.showPaymentErrorModal() // Callback que se ejecuta cuando hay error en el pago
      );
      
      this.paymentFormLoaded = true;
      this.paymentFormError = false;
      
    } catch (error) {
      console.error('Error al cargar formulario de Alignet:', error);
      this.paymentFormError = true;
      this.paymentFormLoaded = false;
      
      // Mostrar error en el formulario
      loadingEl.style.display = "none";
      target.style.display = "block";
      target.innerHTML = `
        <div style="padding: 20px; text-align: center; border: 2px dashed #dc2626; border-radius: 8px; background: #fef2f2;">
          <h4 style="color: #dc2626; margin-bottom: 12px;">Error al cargar formulario</h4>
          <p style="color: #991b1b; font-size: 14px;">No se pudo cargar la pasarela de pago. Inténtalo de nuevo.</p>
          <button onclick="location.reload()" style="margin-top: 12px; padding: 8px 16px; background: #dc2626; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Reintentar
          </button>
        </div>
      `;
    }
  }

  async obtenerAccessToken(): Promise<string> {
    // Simulación de obtención de token
    return new Promise((resolve) => {
      setTimeout(() => resolve("mock_access_token"), 500);
    });
  }

  async obtenerNonce(token: string): Promise<string> {
    // Simulación de obtención de nonce
    return new Promise((resolve) => {
      setTimeout(() => resolve("mock_nonce"), 500);
    });
  }

  // Método para cargar el formulario cuando se va al step 2
  async onGoToPayment(): Promise<void> {
    this.currentStep = 2;
    this.paymentFormLoaded = false;
    
    // Si el monto final es 0, no cargar el formulario de pago
    const montoFinal = this.getFinalAmount();
    if (montoFinal === 0) {
      return;
    }
    
    // Esperar a que el DOM se actualice
    setTimeout(async () => {
      const target = document.getElementById('payment-form-container');
      const loading = document.getElementById('payment-loading');
      
      if (target && loading) {
        // Convertir el monto a decimal con 2 dígitos
        const montoConDecimales = parseFloat(montoFinal.toFixed(2));
        await this.cargarFormulario(target, loading, montoConDecimales);
      }
    }, 100);
  }

  // Función para abrir el modal de pago y cargar el formulario
  async abrirModal(monto: number): Promise<void> {
    try {
      // Usar el monto final (con descuento aplicado si existe)
      const montoFinal = this.finalAmount > 0 ? this.finalAmount : monto;
      
      // Convertir el monto a decimal con 2 dígitos
      const montoConDecimales = parseFloat(montoFinal.toFixed(2));
      
      // Mostrar el modal de pago (ya está visible en el step 2)
      console.log('Abriendo modal de pago con monto:', montoConDecimales);
      
      // Obtener los elementos del DOM
      const target = document.getElementById('payment-form-container');
      const loading = document.getElementById('payment-loading');
      
      if (target && loading) {
        // Cargar el formulario de pago con el monto convertido a decimal
        await this.cargarFormulario(target, loading, montoConDecimales);
      } else {
        console.error('No se encontraron los elementos del formulario de pago');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar el formulario de pago',
          confirmButtonText: 'Entendido'
        });
      }
    } catch (error) {
      console.error('Error al abrir modal de pago:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar el formulario de pago',
        confirmButtonText: 'Entendido'
      });
    }
  }

  // Función para aplicar código de descuento
  async aplicarDescuento(): Promise<void> {
    if (!this.discountCode.trim()) {
      Swal.fire({
        title: 'Código requerido',
        text: 'Por favor, ingresa un código de descuento.',
        icon: 'warning',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    this.isApplyingDiscount = true;

    try {
      const response = await this.discountService.validateDiscountCode(this.discountCode.trim()).toPromise();
      
      if (response?.success && response.discount) {
        // Código válido
        this.appliedDiscount = response.discount;
        
        // Si se aplica un descuento manual, desactivar el checkbox de DESCEXECUTIVE
        if (this.useExecutiveDiscount) {
          this.useExecutiveDiscount = false;
        }
        
        // Calcular el monto base usando el costo del experto
        const costoPorHora = this.getExpertCost();
        const montoBase = this.selectedTimeSlots.length * costoPorHora;
        
        console.log('Debug descuento:');
        console.log('Costo por hora:', costoPorHora);
        console.log('Horarios seleccionados:', this.selectedTimeSlots.length);
        console.log('Monto base:', montoBase);
        console.log('Descuento recibido:', response.discount);
        
        // Calcular el descuento
        this.discountAmount = this.discountService.calculateDiscountAmount(montoBase, response.discount);
        
        // Calcular el monto final
        this.finalAmount = this.discountService.applyDiscount(montoBase, response.discount);
        
        console.log('Descuento calculado:', this.discountAmount);
        console.log('Monto final:', this.finalAmount);
        
        // Mostrar mensaje de éxito
        Swal.fire({
          title: '¡Descuento aplicado!',
          text: `Ahorras S/ ${this.discountAmount.toFixed(2)}`,
          icon: 'success',
          confirmButtonText: 'Genial'
        });
        
        // Recargar el formulario de pago con el monto final (con descuento aplicado)
        setTimeout(async () => {
          const target = document.getElementById('payment-form-container');
          const loading = document.getElementById('payment-loading');
          
          if (target && loading) {
            // Convertir el monto final a decimal con 2 dígitos
            const montoConDecimales = parseFloat(this.finalAmount.toFixed(2));
            await this.cargarFormulario(target, loading, montoConDecimales);
          }
        }, 100);
        
        // Forzar actualización de la vista
        this.cdr.detectChanges();
        
      } else {
        // Código inválido
        this.appliedDiscount = null;
        this.discountAmount = 0;
        this.finalAmount = 0;
        
        const mensaje = response?.message || 'Código de descuento no válido';
        Swal.fire({
          title: 'Código inválido',
          text: mensaje,
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
      
    } catch (error) {
      console.error('Error al aplicar descuento:', error);
      this.appliedDiscount = null;
      this.discountAmount = 0;
      this.finalAmount = 0;
      
      Swal.fire({
        title: 'Error',
        text: 'Error al validar el código de descuento. Inténtalo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      this.isApplyingDiscount = false;
    }
  }

  // Función para remover descuento
  removerDescuento(): void {
    this.discountCode = '';
    this.appliedDiscount = null;
    this.discountAmount = 0;
    this.finalAmount = 0;
    
    // Recargar el formulario de pago con el monto original (sin descuento)
    setTimeout(async () => {
      const target = document.getElementById('payment-form-container');
      const loading = document.getElementById('payment-loading');
      
      if (target && loading) {
        // Calcular el monto original sin descuento
        const costoPorHora = this.getExpertCost();
        const montoOriginal = this.selectedTimeSlots.length * costoPorHora;
        const montoConDecimales = parseFloat(montoOriginal.toFixed(2));
        
        // Formatear el monto para Alignet
        
        await this.cargarFormulario(target, loading, montoConDecimales);
      }
    }, 100);
    
    this.cdr.detectChanges();
  }

  // Obtener las descripciones profesionales como array
  getProfessionalDescriptions(): string[] {
    if (!this.mentor?.coachProfileData?.professionalDescription) {
      return [];
    }
    
    // Si es un array, devolverlo directamente
    if (Array.isArray(this.mentor.coachProfileData.professionalDescription)) {
      return this.mentor.coachProfileData.professionalDescription;
    }
    
    // Si es un string, dividirlo por comas (para compatibilidad con datos antiguos)
    if (typeof this.mentor.coachProfileData.professionalDescription === 'string') {
      return this.mentor.coachProfileData.professionalDescription
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    
    return [];
  }

  // Obtener los resúmenes de experiencia como array
  getExperienceSummaries(): string[] {
    if (!this.mentor?.coachProfileData?.experienceSummary) {
      return [];
    }
    
    // Si es un array, devolverlo directamente
    if (Array.isArray(this.mentor.coachProfileData.experienceSummary)) {
      return this.mentor.coachProfileData.experienceSummary;
    }
    
    // Si es un string, dividirlo por comas (para compatibilidad con datos antiguos)
    if (typeof this.mentor.coachProfileData.experienceSummary === 'string') {
      return this.mentor.coachProfileData.experienceSummary
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    
    return [];
  }

  // ===== NAVEGACIÓN =====
  goBack(): void {
    this.router.navigate(["/dashboard/mentors"]);
  }

  // ===== GETTERS =====
  get hasSpecialtyTags(): boolean {
    return !!(this.mentor?.coachProfileData?.specialtyTags && this.mentor.coachProfileData.specialtyTags.length > 0);
  }

  // Obtener las categorías del experto (nuevo esquema múltiple o antiguo singular)
  getExpertCategories(): { id: string | number, name: string, cost?: number }[] {
    if (this.mentor?.type?.expertCategories && Array.isArray(this.mentor.type.expertCategories) && this.mentor.type.expertCategories.length > 0) {
      // Nuevo esquema: array de categorías
      return this.mentor.type.expertCategories;
    } else if (this.mentor?.type?.expertCategory) {
      // Esquema antiguo: categoría única (compatibilidad)
      return [this.mentor.type.expertCategory];
    }
    return [];
  }

  // Obtener las categorías para mostrar (primera categoría o todas separadas por comas)
  getExpertCategoriesDisplay(): string {
    const categories = this.getExpertCategories();
    if (categories.length === 0) {
      return '';
    }
    // Mostrar todas las categorías separadas por comas
    return categories.map(cat => cat.name).join(', ');
  }

  // Obtener el costo por hora del experto (del nuevo esquema o antiguo)
  getExpertCost(): number {
    const categories = this.getExpertCategories();
    if (categories.length > 0 && categories[0].cost) {
      return categories[0].cost;
    }
    // Valor por defecto si no se encuentra el costo
    return 200;
  }

  // Calcular el monto total sin descuento
  getTotalAmount(): number {
    const costoPorHora = this.getExpertCost();
    return this.selectedTimeSlots.length * costoPorHora;
  }

  // Calcular el monto final (con descuento si existe)
  getFinalAmount(): number {
    let monto = this.getTotalAmount();
    
    // Aplicar descuento manual si existe
    if (this.appliedDiscount && this.discountAmount > 0) {
      monto = monto - this.discountAmount;
    }
    
    // Si se usa el descuento DESCEXECUTIVE, el monto es 0
    if (this.useExecutiveDiscount && this.executiveDiscount) {
      monto = 0;
    }
    
    return Math.max(0, monto);
  }

  /**
   * Carga el descuento DESCEXECUTIVE del usuario
   */
  loadExecutiveDiscount(): void {
    const currentUser = this.authService.getAuthUser();
    if (!currentUser?.id) {
      return;
    }

    this.isLoadingExecutiveDiscount = true;
    
    this.discountService.getUserDiscountByCode('DESCEXECUTIVE').subscribe({
      next: (response) => {
        if (response?.success && response.discount) {
          // Validar disponibilidad: limiteUso >= usosActuales
          const limiteUso = response.discount.limiteUso || 0;
          const usosActuales = response.discount.usosActuales || 0;
          
          if (limiteUso === 0 || limiteUso > usosActuales) {
            this.executiveDiscount = response.discount;
          } else {
            this.executiveDiscount = null;
          }
        } else {
          this.executiveDiscount = null;
        }
        this.isLoadingExecutiveDiscount = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error al cargar descuento DESCEXECUTIVE:', error);
        this.executiveDiscount = null;
        this.isLoadingExecutiveDiscount = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Maneja el cambio del checkbox de usar descuento DESCEXECUTIVE
   */
  onUseExecutiveDiscountChange(): void {
    if (this.useExecutiveDiscount && this.executiveDiscount) {
      // Si se activa, desactivar el descuento manual si existe
      if (this.appliedDiscount) {
        this.removerDescuento();
      }
      
      // Recargar el formulario de pago con monto 0
      setTimeout(async () => {
        const target = document.getElementById('payment-form-container');
        const loading = document.getElementById('payment-loading');
        
        if (target && loading) {
          await this.cargarFormulario(target, loading, 0);
        }
      }, 100);
    } else {
      // Si se desactiva, recargar con el monto original
      setTimeout(async () => {
        const target = document.getElementById('payment-form-container');
        const loading = document.getElementById('payment-loading');
        
        if (target && loading) {
          const costoPorHora = this.getExpertCost();
          const montoOriginal = this.selectedTimeSlots.length * costoPorHora;
          const montoConDecimales = parseFloat(montoOriginal.toFixed(2));
          await this.cargarFormulario(target, loading, montoConDecimales);
        }
      }, 100);
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Obtiene los usos disponibles del descuento DESCEXECUTIVE
   */
  getExecutiveDiscountAvailableUses(): number {
    if (!this.executiveDiscount) return 0;
    const limiteUso = this.executiveDiscount.limiteUso || 0;
    const usosActuales = this.executiveDiscount.usosActuales || 0;
    return Math.max(0, limiteUso - usosActuales);
  }

  /**
   * Obtiene el número del uso que se va a realizar (usosActuales + 1)
   */
  getExecutiveDiscountNextUse(): number {
    if (!this.executiveDiscount) return 0;
    const usosActuales = this.executiveDiscount.usosActuales || 0;
    return usosActuales + 1;
  }

  /**
   * Completa la agenda sin pasar por el proceso de pago (cuando el monto es 0)
   */
  completeFreeAppointment(): void {
    if (!this.mentor?._id || !this.selectedCalendarDay || this.selectedTimeSlots.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Faltan datos para reservar la cita',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    if (!this.mentor.type || (this.mentor.type.name !== 'Coach' && this.mentor.type.name !== 'Experto')) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'El usuario seleccionado no es un mentor o Experto válido',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const currentUser = this.authService.getAuthUser();
    if (!currentUser) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Usuario no autenticado',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const userType = this.mentor?.type?.name === 'Coach' ? 'mentor' : 'Experto';
    const selectedDate = formatDate(this.selectedCalendarDay.date, 'yyyy-MM-dd', 'en-US');
    
    // Crear array de citas para reservar
    const appointmentsToBook = this.selectedTimeSlots.map(slot => ({
      date: selectedDate,
      time: this.convertTimeTo12Hour(slot.displayTime), // Asegurar formato 12h con AM/PM
      clientName: currentUser.name || 'Cliente',
      clientId: currentUser.id
    }));

    // Si se está usando el descuento DESCEXECUTIVE, incrementar el contador de usos
    if (this.useExecutiveDiscount && this.executiveDiscount) {
      this.discountService.incrementDiscountUsage('DESCEXECUTIVE').subscribe({
        next: () => {
          console.log('Uso del descuento DESCEXECUTIVE incrementado');
          // Recargar el descuento para actualizar los usos disponibles
          this.loadExecutiveDiscount();
        },
        error: (error) => {
          console.error('Error al incrementar uso del descuento DESCEXECUTIVE:', error);
        }
      });
    }

    // Reservar todas las citas seleccionadas
    let completedBookings = 0;
    let failedBookings = 0;
    // Array para almacenar los appointmentIds de las citas reservadas
    const bookedAppointments: Array<{appointmentId: string, date: string, time: string}> = [];

    appointmentsToBook.forEach((appointmentData, index) => {
      this.usersService.bookAppointment(
        this.mentor!._id, 
        appointmentData, 
        currentUser.id
      ).subscribe({
        next: (response) => {
          console.log(`Cita ${index + 1} reservada exitosamente:`, response);
          completedBookings++;
          
          // Obtener el appointmentId de la respuesta
          const mentorData = response.data;
          if (mentorData?.coachAppointments && mentorData.coachAppointments.length > 0) {
            const lastAppointment = mentorData.coachAppointments[mentorData.coachAppointments.length - 1] as any;
            if (lastAppointment?.appointmentId) {
              bookedAppointments.push({
                appointmentId: lastAppointment.appointmentId,
                date: appointmentData.date,
                time: appointmentData.time
              });
            }
          }
          
          // Si todas las citas se completaron, mostrar modal de éxito y enviar emails
          if (completedBookings === appointmentsToBook.length) {
            this.closePurchaseSummaryModal();
            this.showSuccessModal = true;
            // Resetear el checkbox del descuento DESCEXECUTIVE
            this.useExecutiveDiscount = false;
            
            // Enviar emails automáticamente al cliente y al experto
            this.sendAppointmentEmails(bookedAppointments, currentUser);
          }
        },
        error: (error) => {
          console.error(`Error al reservar la cita ${index + 1}:`, error);
          failedBookings++;
          
          // Si todas las citas fallaron, mostrar error
          if (failedBookings === appointmentsToBook.length) {
            Swal.fire({
              icon: 'error',
              title: 'Error al reservar citas',
              text: `Error al reservar las citas con tu ${userType}: ${error.error?.message || 'Error desconocido'}`,
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#d33'
            });
          }
        }
      });
    });
  }

  /**
   * Envía emails automáticamente al cliente y al experto con los detalles de las citas
   */
  private sendAppointmentEmails(
    bookedAppointments: Array<{appointmentId: string, date: string, time: string}>,
    currentUser: any
  ): void {
    if (!this.mentor || bookedAppointments.length === 0) {
      console.warn('No se pueden enviar emails: faltan datos del mentor o citas');
      return;
    }

    const clientEmail = currentUser.email;
    const mentorEmail = this.mentor.email;

    if (!clientEmail || !mentorEmail) {
      console.warn('No se pueden enviar emails: faltan emails del cliente o del experto');
      return;
    }

    // Enviar email para cada cita reservada
    bookedAppointments.forEach((appointment, index) => {
      // Generar meetingLink usando el appointmentId
      const meetingLink = `${environment.meetingUrl}${appointment.appointmentId}`;
      
      // Convertir tiempo de formato 12h (08:00 PM) a formato 24h (20:00) para el calendario
      const convertTo24Hour = (time12h: string): string => {
        const [time, period] = time12h.split(' ');
        const [hours, minutes] = time.split(':');
        let hour24 = parseInt(hours, 10);
        
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }
        
        return `${hour24.toString().padStart(2, '0')}:${minutes}`;
      };
      
      const time24h = convertTo24Hour(appointment.time);
      
      // Formatear la fecha para mostrar en el email
      const formattedDate = new Date(appointment.date).toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Email para el cliente
      const clientEmailData: EmailData = {
        to: clientEmail,
        subject: `Confirmación de cita con ${this.mentor?.name || 'Experto'} - Phoenix`,
        template: 'purchase-notification',
        data: {
          clientName: currentUser.name || 'Cliente',
          mentorName: this.mentor?.name || 'Experto',
          sessionDate: appointment.date, // Enviar en formato YYYY-MM-DD para el calendario
          sessionTime: time24h, // Enviar en formato 24h para el calendario
          meetingLink: meetingLink,
          meetingId: appointment.appointmentId,
          amount: this.getFinalAmount(),
          currency: 'S/',
          transactionId: appointment.appointmentId
        }
      };

      // Email para el experto
      const mentorEmailData: EmailData = {
        to: mentorEmail,
        subject: `Nueva cita agendada con ${currentUser.name || 'Cliente'} - Phoenix`,
        template: 'purchase-notification',
        data: {
          clientName: currentUser.name || 'Cliente',
          mentorName: this.mentor?.name || 'Experto',
          sessionDate: appointment.date, // Enviar en formato YYYY-MM-DD para el calendario
          sessionTime: time24h, // Enviar en formato 24h para el calendario
          meetingLink: meetingLink,
          meetingId: appointment.appointmentId,
          amount: this.getFinalAmount(),
          currency: 'S/',
          transactionId: appointment.appointmentId
        }
      };

      // Enviar email al cliente
      this.emailService.sendPurchaseNotification(clientEmailData).subscribe({
        next: (response) => {
          console.log(`✅ Email enviado al cliente para cita ${index + 1}:`, response);
        },
        error: (error) => {
          console.error(`❌ Error al enviar email al cliente para cita ${index + 1}:`, error);
        }
      });

      // Enviar email al experto
      this.emailService.sendPurchaseNotification(mentorEmailData).subscribe({
        next: (response) => {
          console.log(`✅ Email enviado al experto para cita ${index + 1}:`, response);
        },
        error: (error) => {
          console.error(`❌ Error al enviar email al experto para cita ${index + 1}:`, error);
        }
      });
    });
  }

  get specialtyTags(): string[] {
    return this.mentor?.coachProfileData?.specialtyTags || [];
  }

  hasEvents(): boolean {
    return (
      !!this.mentor?.coachProfileData?.eventsAndAppearances &&
      this.mentor?.coachProfileData?.eventsAndAppearances.length > 0
    );
  }

  hasMultipleEvents(): boolean {
    return (
      !!this.mentor?.coachProfileData?.eventsAndAppearances &&
      this.mentor?.coachProfileData?.eventsAndAppearances.length > 1
    );
  }

  hasAppointments(): boolean {
    return (
      !!this.mentor?.coachAppointments &&
      this.mentor?.coachAppointments.length > 0
    );
  }

  hasNoEventsOrAppointments(): boolean {
    return (
      (!this.mentor?.coachProfileData?.eventsAndAppearances ||
        this.mentor?.coachProfileData?.eventsAndAppearances.length === 0) &&
      (!this.mentor?.coachAppointments ||
        this.mentor?.coachAppointments.length === 0)
    );
  }

  getProfilePhotoUrl(): string {
    if (this.mentor?.personalSection?.photoBase64) {
      return this.mentor.personalSection.photoBase64;
    }
    return this.mentor?.photo || "assets/img/user-photo-default.png";
  }

  formatTextWithBreaks(text: string | undefined): string {
    if (!text) {
      return 'Licenciado en nutrición por la Universidad Peruana Cayetano Heredia y colegiatura habilitado (CNP 9236). Con experiencia en el tratamiento clínico especializado en obesidad y diabetes, diabéticos, hipotiroidismo, enfermedades cardiovasculares, entre otros. Adicionalmente, se ha desempeñado también en áreas de salud comunitaria, siendo parte de proyectos orientados a la educación alimentaria nutricional en colegios estatales, en la Fundación Acción Contra el Hambre, donde ha desarrollado talleres de capacitación virtuales y presenciales a población vulnerable migrante.';
    }
    return text.replace(/\n/g, '<br>');
  }

  /**
   * Verifica si el usuario es Executive
   */
  isExecutiveUser(): boolean {
    const currentUser = this.authService.getUser();
    return !!(
      currentUser &&
      currentUser.type &&
      currentUser.type.id === 1 && // Customer
      currentUser.type.suscriptionType &&
      currentUser.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  /**
   * Obtiene la ruta correcta para el dashboard
   */
  getDashboardRoute(): string {
    return this.isExecutiveUser() ? '/dashboard/dashboard-executive' : '/dashboard/dashboard-menu';
  }

  /**
   * Normaliza la URL de LinkedIn para asegurar que tenga el protocolo https://
   */
  normalizeLinkedInUrl(url: string | undefined | null): string {
    if (!url) {
      return '';
    }
    
    // Si ya tiene protocolo, retornarlo tal cual
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Si no tiene protocolo, agregar https://
    return `https://${url}`;
  }
}
